import React from "react";
import { renderers } from "./renderers";
import { useStudy } from "../../pages/study/hooks";
import {
  isValidKeywordsMarkupContent,
  isValidMarkupContent,
  parseContentMarkupToJSON,
  parseKeywordsMarkupToJSON,
} from "../markupEditor";

const CONTENT_SEPARATOR = ";";

// Chapter types whose whole purpose is reading a passage aloud. Only these are
// worth flagging when they resolve to no sentences — an assessment or recap
// chapter legitimately carries no readable content.
const READING_CHAPTER_TYPES = [
  "story-with-keywords",
  "pronounciation-summary",
  "story",
];

export default function ChapterRenderers({
  chapterType,
  data,
  sectionId,
  sectionType,
  courseId,
  style,
  onComplete,
  activities,
  onNextChapter,
  ...props
}) {
  const { setProgress, progress, loadSynonyms } = useStudy();
  const [chapter, setChapter] = React.useState({});
  const ref = React.useRef(null);

  const parseTypes = (str) =>
    String(str).trim().replace(/\s+/g, "-").toLowerCase();

  chapterType = parseTypes(chapterType);
  sectionType = parseTypes(sectionType);

  const Renderer = renderers[chapterType];

  const parseImportantWords = (keywords) => {
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords;

    if (isValidKeywordsMarkupContent(keywords)) {
      let _keywords = parseKeywordsMarkupToJSON(keywords);
      if (_keywords.length) {
        return _keywords.map((e) => String(e.text).trim()).filter((e) => e);
      }
    }

    let words = keywords.split(",").map((e) => e.trim());
    return words.filter((e) => e);
  };

  // Returns a NEW chapter object — never mutates the one it is given. The old
  // version wrote the `;`-joined plain text back onto `data.content`, destroying
  // the markup on an object that is also reachable from Sectionv2; re-parsing
  // that same object then silently fell into the plain-text branch and dropped
  // parsedMarkupContent.
  const parseContent = (data = {}) => {
    let contentArray = [],
      parsedMarkupContent = data.parsedMarkupContent,
      parsedmarkupKeywords = data.parsedmarkupKeywords,
      content = data.content;

    // Markup detection is driven by the CONTENT, not by contentType. The lesson
    // plan editor lets an author pick any content type for a session, and the
    // "Story with Keywords" editor never forces "Text" — so gating on
    // contentType silently skipped the parse for perfectly valid markup, leaving
    // the story pane blank and the speech evaluator with no sentence to score.
    if (data.content && !Array.isArray(data.content)) {
      if (isValidMarkupContent(data.content)) {
        let parsed = parseContentMarkupToJSON(data.content);
        let parsedKeywords = parseKeywordsMarkupToJSON(data.keyWords);

        contentArray = parsed.map((e) => e.text);

        parsedMarkupContent = parsed;
        parsedmarkupKeywords = parsedKeywords;
        content = contentArray.join(CONTENT_SEPARATOR);
      } else if (String(data.contentType).toLowerCase() == "text") {
        // Legacy plain-text chapters: sentences separated by CONTENT_SEPARATOR.
        contentArray = (
          data.content.split(CONTENT_SEPARATOR).map((e) => e.trim()) || []
        ).filter((e) => e);
        content = contentArray.join(CONTENT_SEPARATOR);
      }
      // contentArray = contentArray.slice(0, 4);
    }

    return {
      ...data,
      content,
      contentArray: contentArray || [],
      parsedMarkupContent,
      parsedmarkupKeywords,
    };
  };

  React.useEffect(() => {
    if (!data || !Object.keys(data).length) return;

    const parsed = parseContent(data);
    parsed.keyWords = parseImportantWords(parsed.keyWords);

    // A reading chapter with nothing readable is a data problem, not a UI state:
    // log enough to identify the offending session from a single console line.
    if (
      READING_CHAPTER_TYPES.includes(chapterType) &&
      !parsed.contentArray.length
    ) {
      console.error("CHAPTER | no readable content", {
        courseId,
        sectionId,
        chapterId: parsed.chapterId,
        chapterType,
        contentType: parsed.contentType,
        content: parsed.content,
      });
    }

    setChapter(parsed);
    setProgress({ chapter: parsed.chapterId });

    // Course-configured keywords (markup) are authoritative — skip the dictionary
    // API call for them. Passing empty words also clears any stale synonyms left
    // in redux from a previous chapter. Only legacy chapters without configured
    // keywords fall back to the per-word dictionary lookup.
    const hasConfiguredKeywords =
      parsed.parsedmarkupKeywords && parsed.parsedmarkupKeywords.length;
    loadSynonyms({ words: hasConfiguredKeywords ? [] : parsed.keyWords });
  }, [data]);

  React.useEffect(() => {
    const handleEvent = (e) => {
      console.log("Copy paste event caught: ", e, ref.current);
      if (ref.current && ref.current.contains(e.target)) {
        e.preventDefault();
      }
    };

    document.addEventListener("copy", handleEvent);
    document.addEventListener("paste", handleEvent);

    return () => {
      document.removeEventListener("copy", handleEvent);
      document.removeEventListener("paste", handleEvent);
    };
  }, []);

  if (!Object.keys(chapter).length) return <></>;

  style = { ...style, justifyContent: "center", alignItems: "center" };

  return Renderer ? (
    <div ref={ref}>
      <Renderer
        {...{
          data: chapter,
          sectionId,
          sectionType,
          courseId,
          style,
          onComplete,
          activities,
          onNextChapter,
        }}
      />
    </div>
  ) : null;
}
